import { AppendDMSegmentType, type IAppendDMSegment } from '@/cc/constants';

import { version } from '../../package.json';
import { MsgName } from './constants';

/**
 * query str to object
 */
export const getQueryObj = (queryString = location.search): Record<string, string> =>
  Array.from(new URLSearchParams(queryString)).reduce((a, [k, v]) => ((a[k] = decodeURIComponent(v)), a), {});

/**
 * query object to str
 */
export const getQueryStr = (data = {}): string =>
  Object.keys(data)
    .map((k) => `${k}=${encodeURIComponent(data[k])}`)
    .join('&');

/** 默认帧率 */
export const DEFAULT_FPS = 60;

/** 是否为debug模式：显示左下角帧率 */
export const IS_DEBUG = !!Number(getQueryObj().cc_debug);

/** 默认CC版本, 此版本为线上使用的版本 */
export const DEFAULT_CC_ID = 'P2025-0407-1011';

/** 进行引擎时迭代灰度时使用的CC_ID，一般配合实验使用，全量后同步到DEFAULT_CC_ID字段，由config.isEngineTune决定是否使用 */
export const NEXT_CC_ID = 'P2025-0407-1011';

/** 默认CC引擎后台版本号 */
export const DEFAULT_CC_ENGINE_VERSION = '2.1.10';

/** 当前使用的CC引擎后台版本号 */
export const curCCEngineVersion = getQueryObj().cc_eid || DEFAULT_CC_ENGINE_VERSION;

/** SDK版本号 */
export const PACKAGE_VERSION = version;

/**
 * 获取当前使用的CC版本
 */
const getCCId = (isNextCCId: boolean) => getQueryObj().cc_id || (isNextCCId ? NEXT_CC_ID : DEFAULT_CC_ID);

/**
 * 根据cc_id的版本号确定CC资源的domain
 * 判断指定的CC静态资源的环境. 根据cc_id的版本号头字母来判断，如果为TXXXXX则代表测试环境，如果为PXXXXX则代表正式环境
 */
const getCCResourceDomain = (CCId) =>
  CCId[0] === 'T' ? `https://vfiles.sparta.html5.qq.com` : `https://vfiles.gtimg.cn`;

/**
 * 获取CC资源加载地址
 */
export const getCCResourceUrl = (isNextCCId: boolean) => {
  const CCId = getCCId(isNextCCId);
  return `${getCCResourceDomain(CCId)}/tvideo/libcocos/${CCId}/web-desktop-main`;
};

/** frame 资源path, 测试版本号（beta）下使用测试环境下的frame资源 */
export const CC_FRAME_PATH = PACKAGE_VERSION.includes('beta')
  ? 'https://mdw.pages.woa.com/libcocos-frame'
  : 'https://vfiles.gtimg.cn/tvideo/libcocos-frame';

/** CC加载超时检测时间 ms*/
export const CC_LOAD_CHECK_TIME = 8000;

/** frame加载超时检测时间 ms*/
export const FRAME_LOAD_CHECK_TIME = 3000;

/**
 * CC协议映射表
 */
export const protocolMap = (key: MsgName) => {
  const map = {
    [MsgName.PLAYER_MSG]: window._MDW_.proto.native.PlayerMsg,
    [MsgName.DANMAKU_SETTING]: window._MDW_.proto.native.DanmakuSetting,
    [MsgName.APPEND_DM_SEGMENT]: {
      encode: (data) => {
        if (data.bizType === AppendDMSegmentType.APPEND_DM_SEGMENT_TYPE_NORMAL) {
          return window._MDW_.proto.native.AppendDMSegment.encode(data);
        }
        return window._MDW_.proto.native.AppendDMSegment.encode(encodeOperationTimepointDm(data));
      },
      originName: MsgName.APPEND_DM_SEGMENT,
    },
    [MsgName.USER_INFO]: window._MDW_.proto.native.UserInfo,
    [MsgName.DM_RICH_DATA_RSP]: window._MDW_.proto.trpc.message.danmu_richdata.DMRichDataRsp,
    [MsgName.DANMAKU_EFFECT]: window._MDW_.proto.com.tencent.qqlive.protocol.pb.GetDMEffectConfigRsp,
    [MsgName.OPERATION]: {
      encode: (data) =>
        window._MDW_.proto.native.Operation.encode({
          operationType: window._MDW_.proto.native.OperationType.OPERATION_TYPE_INSTANT_INSERT_DANMAKU,
          operation: {
            value: window._MDW_.proto.native.InstantInsertDanmaku.encode({
              commentList: [
                {
                  dmComment: {
                    dwFirstTag: 1,
                    operationData: {
                      value: window._MDW_.proto.com.tencent.qqlive.protocol.pb.OperationDMInfo.encode(data).finish(),
                    },
                    operationDmType:
                      window._MDW_.proto.com.tencent.qqlive.protocol.pb.OperationDMType.OPERATION_DM_TYPE_DEFAULT,
                  },
                },
              ],
            }).finish(),
          },
        }),
      originName: MsgName.OPERATION,
    },
    [MsgName.OPERATION_POST]: {
      encode: (data) =>
        window._MDW_.proto.native.Operation.encode({
          operationType: window._MDW_.proto.native.OperationType.OPERATION_TYPE_INSTANT_INSERT_DANMAKU,
          operation: {
            value: window._MDW_.proto.native.InstantInsertDanmaku.encode({
              commentList: [
                {
                  dmComment: data,
                },
              ],
            }).finish(),
          },
        }),
      originName: MsgName.OPERATION,
    },
    [MsgName.LIVE_DELTA_TIME_CONFIG]: {
      encode: (data) => {
        let unitByte;
        try {
          unitByte = new TextEncoder().encode(JSON.stringify(data));
        } catch (error) {}
        return {
          finish: () => unitByte,
        };
      },
    },
    /** 因为TAB的数据同步没有走之前的引擎协议，所以这里模拟一个encode().finish() */
    [MsgName.TAB_INFO]: {
      encode: (data) => {
        let unitByte;
        try {
          unitByte = new TextEncoder().encode(JSON.stringify(data));
        } catch (error) {}
        return {
          finish: () => unitByte,
        };
      },
    },
    [MsgName.VIDEO_ENVIRONMENT_CONFIG]: window._MDW_.proto.native.VideoEnvironmentConfig,
  };
  return map[key];
};

/**
 * 获取aegis的当前配置
 */
export const getAegisConf = () => {
  const { aid = '', uin = '', ext1 = '', ext2 = '', ext3 = '' } = window.aegis?.config ?? {};
  return { aid, uin, ext1, ext2, ext3 };
};

/**
 * 全屏状态下，5秒内不移动鼠标，自动隐藏鼠标光标
 * TIPS: 引擎侧项目在监听到鼠标事件时，会重设鼠标样式，包含两种样式：hover到弹幕时的pointer和常规样式, 所以插件侧只负责隐藏即可
 */
export function hideMouseAuto() {
  let timer: ReturnType<typeof setTimeout>;
  return function () {
    clearTimeout(timer);
    timer = setTimeout(() => {
      const danmakuElm = document.querySelector('#GameCanvas') as HTMLElement;
      // 通过弹幕尺寸是否为屏幕尺寸来判断是否全屏
      if (danmakuElm?.clientWidth === screen.width) {
        danmakuElm.style.cursor = 'none';
      }
    }, 5 * 1000);
  };
}

/**
 * 解码需要特殊处理弹幕点击回调
 */
export function decodeDmOperationsClickCb(operations) {
  if (!window?._MDW_?.proto) return;
  const { OperationDMActivityType } = window._MDW_.proto.com.tencent.qqlive.protocol.pb;
  const { pb } = window._MDW_.proto.com.tencent.qqlive.protocol;

  operations.operationList.forEach((operation) => {
    const { operationType } = operation;
    switch (operationType) {
      case OperationDMActivityType.OPERATION_DM_ACTIVITY_TYPE_VOTE:
        operation.data = pb.VoteOptionInfo.decode(operation.data.value);
        break;
      case OperationDMActivityType.OPERATION_DM_ACTIVITY_TYPE_PUBLISH:
        operation.data = pb.PublishConfigInfo.decode(operation.data.value);
        break;
      default:
        break;
    }
  });
  if (operations.nextOperationList) {
    decodeDmOperationsClickCb(operations.nextOperationList);
  }
}

/**
 * 编码强插的时间点弹幕
 */
function encodeOperationTimepointDm(data: IAppendDMSegment) {
  // 从@type字符串中提取编码的类
  function encodeDataByType(data) {
    const typeValue = data['@type'];
    const parts = typeValue.split('/');
    const classNameList = parts[parts.length - 1].split('.');
    if (classNameList.length === 0) return;
    const targetClass = classNameList.reduce((acc, cur) => acc?.[cur], window._MDW_.proto);
    return targetClass?.encode(data).finish();
  }
  // 处理dmoperationDataList字段
  function processDmOperationDataList(dataList) {
    if (!dataList) {
      return null;
    }
    const processedList = {};
    for (const key in dataList) {
      const operationList = dataList[key].operationList.map((op) => {
        const processedOp = { ...op };
        if (op.data['@type']) {
          processedOp.data = {
            value: encodeDataByType(op.data),
          };
        }
        return processedOp;
      });
      processedList[key] = { operationList };
    }
    return processedList;
  }
  // 处理activityData字段
  function processActivityData(activityData) {
    if (!activityData) {
      return null;
    }
    const { Any } = window._MDW_.proto.google.protobuf;
    const voteItems = activityData?.voteItems.map((v) => {
      const processedOpList = {
        operationList: v.operationList.operationList.map((op) => {
          const processedOp = { ...op };
          if (op.data['@type']) {
            processedOp.data = {
              value: encodeDataByType(op.data),
            };
          }
          return processedOp;
        }),
      };
      return {
        ...v,
        operationList: processedOpList,
      };
    });

    return Any.create({
      value: encodeDataByType({
        ...activityData,
        voteItems,
      }),
    });
  }
  return {
    ...data,
    dmSegment: {
      commentList: data.dmSegment.commentList.map((d) => ({
        ...d,
        operationData: window._MDW_.proto.google.protobuf.Any.create({
          value: window._MDW_.proto.com.tencent.qqlive.protocol.pb.OperationDMInfo.encode({
            ...d.operationData,
            dmoperationDataList: processDmOperationDataList((d.operationData as any).dmoperationDataList),
            activityData: processActivityData((d.operationData as any).activityData),
          }).finish(),
        }),
      })),
    },
  };
}
